import { EyeFilled, HeartFilled, HeartOutlined } from '@ant-design/icons';
import { SailBoatIcon } from '@marine/icons';
import { Avatar, AvatarProps, Button, Card, Typography } from 'antd';
import cn from 'classnames';
import { isValidElement, ReactElement, ReactNode, useState } from 'react';
import { ReactCountryFlag } from 'react-country-flag';
import { useFilesUrlWithToken } from 'services';
import NoImage from './assets/no-image-placeholder.svg';
import styles from './public-boat-card.module.scss';

const { Paragraph, Title } = Typography;

export type PublicBoatCardProps = {
  /**
   * Boat name to be rendered as card title.
   */
  title: string;
  /**
   * Optional className to style card Wrapper.
   */
  className?: string;
  /**
   * Views count.
   */
  views?: number;
  /**
   * favorites count.
   */
  favorites_count?: number;
  /**
   * Optional config to remove shadow (Default true).
   */
  hasShadow?: boolean;
  /**
   * URL for boat image or conditionally render ReactElement.
   */
  boatImage?: string | ReactElement | null;
  /**
   * Optional Flag, just include Country code to render Flag, ex: 'US' | 'Gb' | 'de';
   */
  flag?: string;
  /**
   * Optional Avatar with AntD Avatar props: { name: 'Initials' } | { src: 'URL', name: 'Shows as tooltip when image is provided' };
   */
  avatar?: AvatarProps & { name?: ReactNode };
  /**
   * Optional onClick callback for when clicking the card.
   */
  onClick?: () => void;
  /**
   * Optional onClickLike callback for when clicking the Like button.
   */
  onFavoriteClick?: () => void;

  /**
   * Favorite toggle button state
   */
  disableFavoriteButton?: boolean;
  /**
   * Optional isFavorite to show if is liked. (default: false)
   */
  isFavorite?: boolean;
  /**
   * Optional Boat model or description with ellipsis.
   */
  boatModel?: string;

  showFavoriteToggle?: boolean;

  boatId?: string;
};

export const PublicBoatCard = ({
  className,
  title,
  isFavorite = false,
  onClick,
  boatImage,
  flag,
  hasShadow = true,
  favorites_count,
  onFavoriteClick,
  disableFavoriteButton = false,
  avatar,
  views,
  boatModel,
  showFavoriteToggle = false,
  boatId,
  ...rest
}: PublicBoatCardProps) => {
  const [errorLoadingImage, setErrorLoadingImage] = useState(false);
  const { fileUrlWithCroppingParamAndToken } = useFilesUrlWithToken();

  let image = <NoImage />;

  if (boatImage && typeof boatImage === 'string') {
    image = (
      <img
        src={fileUrlWithCroppingParamAndToken(boatImage, { format: 'card' }) || ''}
        alt={title}
        onError={(e: any) => {
          if (!errorLoadingImage) {
            setErrorLoadingImage(true);
            (e.target as HTMLImageElement).onerror = null;
            (e.target as HTMLImageElement).src = NoImage;
          }
        }}
      />
    );
  } else if (boatImage && isValidElement(boatImage)) {
    image = boatImage;
  }

  const cover = (
    <div className={styles.cover}>
      {image}

      <div
        className={cn(styles.stats, {
          [styles.statsShadow]:
            (hasShadow && boatImage && (views !== null || favorites_count !== null)) || (hasShadow && avatar),
        })}
      >
        {views ? (
          <div className={styles.statsItem}>
            <EyeFilled />
            <p>{views.toLocaleString()}</p>
          </div>
        ) : null}

        {favorites_count ? (
          <div className={styles.statsItem}>
            <HeartFilled />
            <p>{favorites_count.toLocaleString()}</p>
          </div>
        ) : null}

        {avatar && (
          <div className={cn(styles.statsItem, styles.avatar)}>
            <Avatar {...avatar}>{avatar?.name || avatar?.children}</Avatar>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`${styles.cardWrapper} ${className || ''}`}
      onClick={onClick}
      onKeyDown={() => {}}
      role="button"
      tabIndex={0}
      data-cy="public-boat-card"
      data-boat-id={boatId}
    >
      {showFavoriteToggle && (typeof onFavoriteClick === 'function' || isFavorite) && (
        <Button
          icon={isFavorite ? <HeartFilled /> : <HeartOutlined />}
          shape="circle"
          className={styles.like}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();

            if (typeof onFavoriteClick === 'function') {
              onFavoriteClick();
            }
          }}
          disabled={disableFavoriteButton}
        />
      )}

      <Card className={styles.card} hoverable bordered={false} cover={cover} {...rest}>
        <div className={styles.titleContainer}>
          <div className={styles.title}>
            <Title level={3}>{title}</Title>
          </div>
          {flag && (
            <div className={styles.flag}>
              <ReactCountryFlag
                countryCode={flag}
                svg
                style={{
                  height: 20,
                  width: 'auto',
                  borderRadius: 3,
                }}
                title={flag}
              />
            </div>
          )}
        </div>
        {boatModel && (
          <div className={styles.boatModel}>
            <div className={styles.boatModelIcon}>
              <SailBoatIcon />
            </div>
            <div className={styles.boatModelTitle}>
              <Paragraph ellipsis={{ tooltip: boatModel }}>{boatModel}</Paragraph>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};
